import validator from "is-my-json-valid";
import memoize from "memoizee";
import { SHORT_DATE_FORMAT } from "../utils/date-utils";

var makeValidator = memoize(validator);

var VALIDATOR_OPTIONS = {
	verbose: true,
	formats: {
		"email": /^[^ ]+@[^ ]+$/,
	}
};

export default function HTMLOutput(props, context) {
	const {
		// These must be passed in for everything to work
		// localize,
		localizeFrom,
		// formatDate
	} = context;
	const {
		schema, data
	} = props;

	const title = schema.title || localizeFrom(schema.localization);

	return `
<!DOCTYPE html>
<title>${title}</title>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0">
<style>
body {
	padding: 0;
	margin: 0;
}

section {
	margin-bottom: 0.25rem;
	padding: 1ch;
	border: 1px solid #888b;
}

h1, h2, h3, h4, h5, h6 {
	margin: 0;
	margin-bottom: 0.25rem;
}

p {
	margin: 0;
	margin-bottom: 0.25rem;
}
</style>
${Output({
		schema,
		data,
		depth: 0
	}, context)}
`;
}

function Title(props, context) {
	const {
		localize, localizeFrom
	} = context;
	const {
		schema, depth
	} = props;

	var label;

	if (schema.localization) {
		label = localizeFrom(schema.localization, ".");
	} else if (schema.description) {
		label = localize(schema.description) || schema.description;
	} else if(schema.title) {
		label = schema.title;
	} else {
		return "";
	}

	const headerLevel = Math.min(depth + 1, 6);

	return `
	<h${headerLevel}>${label}</h${headerLevel}>
`;
}

function Output(props, context) {
	let {
		schema,
		data,
		depth
	} = props;

	const type = schema.type;
	const displayType = schema.displayType;
	let Component = Text;

	if (displayType === "hidden")
		return "";
	else if (displayType === "form")
		Component = Form;
	else if (displayType === "list")
		Component = List;
	else if (displayType === "days")
		Component = Days;
	// else if (displayType === "notification")
	// 	Component = Notification;
	// else if (displayType === "files")
	// 	Component = Files;
	// else if (displayType === "file")
	// 	Component = File;
	// else if (displayType === "weight")
	// 	Component = Weight;
	else if (displayType === "nested") {
		var displayProperty = schema.displayProperty;
		var order = schema.properties[displayProperty].order;
		data = data ? data[displayProperty] : null;
		if (order === undefined)
			schema.properties[displayProperty].order = schema.order;
		schema = schema.properties[displayProperty];
		Component = Output;
	} else if (type === "object") {
		Component = Form;
	} else if (type === "array") {
		Component = List;
	} else if (type === "boolean") {
		Component = YesNo;
	}

	return Component({
		schema,
		data,
		depth
	}, context);
}

function Form(props, context) {
	const {
		schema, data, depth
	} = props;

	var subschema = schema.properties || {};
	var dependencies = schema.dependencies || schema.displayDependencies || {};
	var ignoreDependencies = schema.ignoreDependencies;

	var items = Object.keys(subschema)
		.filter(function (propertyName) {
			if(data[propertyName] === undefined) return false;

			if (ignoreDependencies)
				return true;
			var satisfied = true;
			var dependency = dependencies[propertyName];
			var dependencyType = typeof dependency;

			if (dependencyType === "string") satisfied = data[dependency];
			else if (dependency && dependencyType === "object") {
				if (Array.isArray(dependency)) {
					satisfied = dependency.every(function (subDependency) {
						return data[subDependency];
					});
				} else {
					var validate = makeValidator(dependency, VALIDATOR_OPTIONS);
					validate(data);
					satisfied = !validate.errors;
				}
			}

			return (subschema[propertyName].displayType !== "hidden") && satisfied;

		}).sort(function (a, b) {
			var orderA = subschema[a].order;
			var orderB = subschema[b].order;

			return (orderA - orderB) || 0;
		}).map(function (propertyName, index) {
			const itemData = data[propertyName];
			const itemSchema = subschema[propertyName];

			return Output({
				data: itemData,
				schema: itemSchema,
				depth: depth + 1,
				index: index
			}, context);
		}).join("");

	return `
<section>
${Title(props, context)}
${items}
</section>
`;
}

function Text(props, context) {
	let {
		schema, data
	} = props;

	const {
		formatDate,
		localizeFrom,
		localize,
	} = context;

	let finalData = data;

	if (schema.inputType === "date" && data) {
		finalData = formatDate(data, SHORT_DATE_FORMAT, "date");
	} else if ((schema.inputType === "time" || schema.format === "time") && data) {
		finalData = data.hours + ":" + data.minutes;
	} else if(schema.value_localization) {
		finalData = localizeFrom(schema.value_localization, data);
	} else if(schema.value_description) {
		finalData = localize(schema.value_description + "." + data);
	}

	return `
${Title(props, context)}
<p>
	${finalData}
</p>
`;
}

function List(props, context) {
	const {
		schema, data, depth
	} = props;

	const itemScehma = schema.items || {};
	const items = (data || []).map((item, index) => Output({
		schema: itemScehma,
		data: item,
		depth: depth + 1,
		index: index,
	}, context)).join("");

	return `
<section>
	${Title(props, context)}
	${items}
</section>
`;
}

function Days(props, context) {
	const {
		localize
	} = context;
	const {
		schema, data
	} = props;

	var value_description = schema.value_description || null;

	/**
	 * 'reduce' picks out the days of the week that are 'true'.
	 * 'map' handles the the localization.
	 */
	var week = Object.keys(data).reduce((acc, item) => {
		if (data[item]) acc = acc.concat(item);
		if (acc.indexOf("all") >= 0) acc = ["all"];
		return acc;
	}, []).map((item) => {
		if (value_description) {
			return localize(value_description + "." + item) + " ";
		}
		return item + " ";
	});

	return `
${Title(props, context)}
<p>
	${week.join(" ")}
</p>
`;
}

function YesNo(props, context) {
	const {
		localize
	} = context;
	const {
		schema, depth, data
	} = props;

	let toShow = data;

	if(!schema.value_localization && !schema.value_description) {
		toShow = data ? "Yes" : "No";
	}

	return Text({
		schema,
		depth,
		data: toShow
	}, context);
}